import React, { useState, useEffect } from "react";
import { Tag, Table, Button, Tooltip, Popconfirm, Switch, Modal, Input, message, Typography, Space } from "antd";
import { Helmet } from "react-helmet";
import axios from "axios";
import dayjs from "dayjs";
import { CheckOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import RequirePermission from "../components/Common/RequirePermission";

const { Title } = Typography;
const { TextArea } = Input;

// Lists every build registered by ts-signage-lg/scripts/deploy-ipk.js (see
// digital-signage-apis/api/lg_app_versions) and lets an admin approve,
// reject (with a reason), or toggle force-update on one - the LG webOS
// counterpart of TizenAppVersions.jsx, same behavior/shape. Approving (or
// toggling force-update on an already-approved version) is what
// device.service.js's getOrInsertDevice (type === "lg") and
// screen.service.js's playSync (platform === "lg") read live, so those are
// the only actions that actually change what's reported to devices as the
// available update. Rejecting is pure record-keeping.
const LgAppVersions = () => {
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [editPermission, setEditPermission] = useState(false);
    const [deletePermission, setDeletePermission] = useState(false);
    const [rejectModal, setRejectModal] = useState(null); // record being rejected, or null
    const [rejectReason, setRejectReason] = useState("");

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const permissions = userData.permissions || {};
        setEditPermission(permissions["signage.lg_app_version_edit"] === "1");
        setDeletePermission(permissions["signage.lg_app_version_delete"] === "1");
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                process.env.REACT_APP_BASE_URL + `/api/lg-app-versions`,
                { params: { limit: pageSize, offset: (currentPage - 1) * pageSize } }
            );
            setDetails(response.data);
        } catch (error) {
            console.error("Error fetching LG app versions", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize]);

    const handleApprove = async (id) => {
        try {
            setLoading(true);
            await axios.put(
                process.env.REACT_APP_BASE_URL + `/api/lg-app-versions/${id}/approve`
            );
            message.success("Version approved - it will now be served to LG devices.");
            await fetchData();
        } catch (error) {
            console.error("Error approving LG app version", error);
            message.error("Failed to approve version");
        } finally {
            setLoading(false);
        }
    };

    const openRejectModal = (record) => {
        setRejectReason("");
        setRejectModal(record);
    };

    const handleReject = async () => {
        if (!rejectModal) return;
        try {
            setLoading(true);
            await axios.put(
                process.env.REACT_APP_BASE_URL + `/api/lg-app-versions/${rejectModal.id}/reject`,
                { reason: rejectReason }
            );
            message.success("Version rejected.");
            setRejectModal(null);
            await fetchData();
        } catch (error) {
            console.error("Error rejecting LG app version", error);
            message.error("Failed to reject version");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await axios.delete(
                process.env.REACT_APP_BASE_URL + `/api/lg-app-versions/${id}`
            );
            message.success("Version deleted.");
            await fetchData();
        } catch (error) {
            console.error("Error deleting LG app version", error);
            message.error(
                (error.response && error.response.data && error.response.data.data) ||
                "Failed to delete version"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleForceUpdateToggle = async (record, checked) => {
        try {
            setLoading(true);
            await axios.put(
                process.env.REACT_APP_BASE_URL + `/api/lg-app-versions/${record.id}/force-update`,
                { is_force_update: checked }
            );
            message.success(
                checked ? "Force update enabled." : "Force update disabled."
            );
            await fetchData();
        } catch (error) {
            console.error("Error updating force-update flag", error);
            message.error("Failed to update force-update flag");
        } finally {
            setLoading(false);
        }
    };

    const statusColor = { pending: "gold", approved: "green", rejected: "red" };
    const latestApproved = details
        .filter((d) => d.status === "approved")
        .sort((a, b) => new Date(b.approved_at) - new Date(a.approved_at))[0];
    const latestApprovedId = latestApproved ? latestApproved.id : null;

    const columns = [
        {
            title: "Version Number",
            dataIndex: "version_number",
            key: "version_number",
        },
        {
            title: "Build Path",
            dataIndex: "build_path",
            key: "build_path",
            render: (text) => <span className="text-small">{text}</span>,
        },
        {
            title: "CDN URL",
            dataIndex: "cdn_url",
            key: "cdn_url",
            render: (text) => (
                <a href={text} target="_blank" rel="noopener noreferrer" className="text-small">
                    {text}
                </a>
            ),
        },
        {
            title: "Force Update",
            dataIndex: "is_force_update",
            key: "is_force_update",
            render: (text, record) => (
                <Switch
                    checked={text === "yes"}
                    disabled={!editPermission}
                    onChange={(checked) => handleForceUpdateToggle(record, checked)}
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                />
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status, record) => {
                const tag = (
                    <Tag color={statusColor[status] || "default"}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Tag>
                );
                return status === "rejected" && record.rejected_reason ? (
                    <Tooltip title={record.rejected_reason}>{tag}</Tooltip>
                ) : (
                    tag
                );
            },
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (text) => <span className="text-small">{dayjs(text).format("DD-MM-YYYY HH:mm")}</span>,
        },
        {
            title: "Approved By",
            dataIndex: "approved_by_name",
            key: "approved_by_name",
            render: (text) => <span>{text ? text.trim() : ""}</span>,
        },
        {
            title: "Approved At",
            dataIndex: "approved_at",
            key: "approved_at",
            render: (text) => <span className="text-small">{text ? dayjs(text).format("DD-MM-YYYY HH:mm") : ""}</span>,
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                editPermission && (
                    <Space>
                        <Tooltip title="Approve this version">
                            <Popconfirm
                                title="Approve this version? It will start being served to LG devices."
                                onConfirm={() => handleApprove(record.id)}
                                okText="Yes"
                                cancelText="No"
                                disabled={record.status === "approved"}
                            >
                                <Button
                                    icon={<CheckOutlined />}
                                    size="small"
                                    disabled={record.status === "approved"}
                                />
                            </Popconfirm>
                        </Tooltip>
                        <Tooltip title="Reject this version">
                            <Button
                                icon={<CloseOutlined />}
                                size="small"
                                danger
                                disabled={record.status === "rejected"}
                                onClick={() => openRejectModal(record)}
                            />
                        </Tooltip>
                        {deletePermission && (
                            <Tooltip
                                title={
                                    record.id === latestApprovedId
                                        ? "This is the latest approved version - approve a different version (or reject this one) before deleting it"
                                        : "Delete this version (removes its .ipk from S3 too)"
                                }
                            >
                                <Popconfirm
                                    title="Delete this version? This removes its .ipk from S3 too - this can't be undone."
                                    onConfirm={() => handleDelete(record.id)}
                                    okText="Delete"
                                    okButtonProps={{ danger: true }}
                                    cancelText="Cancel"
                                    disabled={record.id === latestApprovedId}
                                >
                                    <Button
                                        icon={<DeleteOutlined />}
                                        size="small"
                                        danger
                                        disabled={record.id === latestApprovedId}
                                    />
                                </Popconfirm>
                            </Tooltip>
                        )}
                    </Space>
                )
            ),
        },
    ];

    return (
        <RequirePermission permission="signage.lg_app_version_view">
            <div style={{ padding: "1rem" }}>
                <Helmet>
                    <title>{`${process.env.REACT_APP_NAME} | LG App`}</title>
                </Helmet>
                <Title level={3} style={{ marginBottom: "1rem" }}>
                    LG App
                </Title>
                <Table
                    loading={loading}
                    columns={columns}
                    dataSource={details}
                    scroll={{ x: "max-content" }}
                    rowKey={(record) => record.id}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                    }}
                />
                <Modal
                    title="Reject Version"
                    open={!!rejectModal}
                    onCancel={() => setRejectModal(null)}
                    onOk={handleReject}
                    okText="Reject"
                    okButtonProps={{ danger: true, disabled: !rejectReason.trim() }}
                >
                    <p>
                        Reject version {rejectModal && rejectModal.version_number}? Please provide a reason.
                    </p>
                    <TextArea
                        rows={3}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Reason for rejection"
                    />
                </Modal>
            </div>
        </RequirePermission>
    );
};

export default LgAppVersions;
